import React from 'react';
import _ from 'lodash';
import { defineMessages } from 'react-intl';
import notAuthorized from '../../not-authorized';
import ViewQuiz from './ViewQuiz';

import { getQuiz } from '../../../actions/viewQuiz';
import {
  getCourseBySlug,
  reset as resetCourse,
} from '../../../actions/courseDetails';
import { getTask, reset as resetTask } from '../../../actions/editGroupTask';
import { getResultAttempts, resetResult } from '../../../actions/quizResult';
import notFound from '../../not-found/index';
import isLoggedIn from '../../../helpers/checkAuth';
import ContextProvider from '../../../components/ContextProvider/ContextProvider';

const messages = defineMessages({
  matchingQuizError: {
    id: 'studentViewOfQuiz.matchingQuizError',
    defaultMessage: 'Cannot find exam by provided quiz id and exam id',
  },
});

async function action(context) {
  const {
    store,
    params,
    fetch,
    query,
    intl: { formatMessage },
  } = context;
  const { courseSlug, quizId, taskId } = params;

  const { auth } = store.getState();

  if (!isLoggedIn(auth)) {
    return notAuthorized(context);
  }

  await store.dispatch(resetCourse());
  if (courseSlug) {
    await store.dispatch({
      type: getCourseBySlug(courseSlug).types[0],
    });

    try {
      const res = await getCourseBySlug(courseSlug).promise(fetch);

      const resText = await res.text();

      const json = resText ? JSON.parse(resText) : {};

      if (res.status !== 200) {
        if ([403, 404].includes(res.status)) {
          return notFound(context);
        }

        await store.dispatch({
          error: json,
          type: getCourseBySlug(courseSlug).types[2],
        });
      } else {
        const result = {
          status: res.status,
          json,
        };
        await store.dispatch({
          result,
          type: getCourseBySlug(courseSlug).types[1],
        });
      }
    } catch (error) {
      const middlewareError = new Error(`MIDDLEWARE ERROR: ${error?.message}`);
      middlewareError.stack = error?.stack;
      console.error(middlewareError);
      await store.dispatch({
        error,
        type: getCourseBySlug(courseSlug).types[2],
      });
    }
  }

  const { courseDetails } = store.getState();
  const courseId = _.get(courseDetails, 'course._id');

  await store.dispatch({
    type: getQuiz(quizId, { courseId, taskId }).types[0],
  });

  try {
    const res = await getQuiz(quizId, { courseId, taskId }).promise(fetch);

    const resText = await res.text();

    const json = resText ? JSON.parse(resText) : {};

    if (res.status !== 200) {
      if ([403, 404].includes(res.status)) {
        return notFound(context);
      }

      await store.dispatch({
        error: json,
        type: getQuiz(quizId, { courseId, taskId }).types[2],
      });
    }

    const result = {
      status: res.status,
      json,
    };

    await store.dispatch({
      result,
      type: getQuiz(quizId, { courseId, taskId }).types[1],
    });
  } catch (error) {
    const middlewareError = new Error(`MIDDLEWARE ERROR: ${error?.message}`);
    middlewareError.stack = error?.stack;
    console.error(middlewareError);
    await store.dispatch({
      error,
      type: getQuiz(quizId, { courseId, taskId }).types[2],
    });
  }

  await store.dispatch(resetTask());
  await store.dispatch(resetResult());
  if (taskId) {
    await store.dispatch({
      type: getTask(taskId).types[0],
    });

    try {
      const res = await getTask(taskId).promise(fetch);

      const resText = await res.text();

      const json = resText ? JSON.parse(resText) : {};

      if (res.status !== 200) {
        if ([403, 404].includes(res.status)) {
          return notFound({
            ...context,
            title: json.message || context.title,
          });
        }

        await store.dispatch({
          error: json,
          type: getTask(taskId).types[2],
        });
      } else {
        const result = {
          status: res.status,
          json,
        };
        await store.dispatch({
          result,
          type: getTask(taskId).types[1],
        });
      }
    } catch (error) {
      const middlewareError = new Error(`MIDDLEWARE ERROR: ${error?.message}`);
      middlewareError.stack = error?.stack;
      console.error(middlewareError);
      await store.dispatch({
        error,
        type: getTask(taskId).types[2],
      });
    }

    const restartQuery = _.has(query, 'restart') ? 'restart' : '';
    await store.dispatch({
      type: getResultAttempts(quizId, { taskId, restartQuery }).types[0],
    });

    try {
      const res = await getResultAttempts(quizId, {
        taskId,
        restartQuery,
      }).promise(fetch);

      const resText = await res.text();

      const json = resText ? JSON.parse(resText) : {};

      if (res.status !== 200) {
        if ([403, 404].includes(res.status)) {
          return notFound(context);
        }

        await store.dispatch({
          error: json,
          type: getResultAttempts(quizId, { taskId, restartQuery }).types[2],
        });
      } else {
        const result = {
          status: res.status,
          json,
        };
        await store.dispatch({
          result,
          type: getResultAttempts(quizId, { taskId, restartQuery }).types[1],
        });
      }
    } catch (error) {
      const middlewareError = new Error(`MIDDLEWARE ERROR: ${error?.message}`);
      middlewareError.stack = error?.stack;
      console.error(middlewareError);
      await store.dispatch({
        error,
        type: getResultAttempts(quizId, { taskId, restartQuery }).types[2],
      });
    }
  }

  const { viewQuiz, editTask } = store.getState();
  const title = `${_.get(viewQuiz, 'quiz.name', '')} - Quiz`;

  if (
    taskId &&
    _.get(editTask, 'task.quizId._id', '').toString() !==
      _.get(viewQuiz, 'quiz._id', '').toString()
  ) {
    return notFound({
      ...context,
      title: formatMessage(messages.matchingQuizError),
    });
  }

  return {
    chunks: ['student-view-quiz'],
    title,
    component: (
      <ContextProvider context={context}>
        <ViewQuiz
          data={{}}
          title={title}
          query={query}
          params={params}
          store={store}
        />
      </ContextProvider>
    ),
  };
}

export default action;
